import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import AxiosObservable from 'axios-observable';
import { refreshToken } from '../redux/auth/service';
import { handleUnauthorized } from './unauthorized.handler';
import { callLogoutSSO, getRefreshToken } from '../redux/auth/auth.functions';
import { ErrorMsg } from './models/Error';

export interface HeadersParams {
  'X-Auth-Token'?: string;
  'X-Company-Id'?: string | null;
  Authorization?: string;
}

interface TryRefreshAndRetryProps<ConfigType, ErrorType> {
  callbackError: (error: ErrorType) => Promise<never>;
  originalError: ErrorType;
  headers: HeadersParams;
  config: ConfigType;
}

let subscribers: ((token: string) => void)[] = [];
const subscribeTokenRefresh = (cb: (token: string) => void) => {
  subscribers = [...subscribers, cb];
};
const onRefreshed = (token: string) => {
  subscribers.forEach(cb => cb(token));
};
export const tryRefreshAndRetry = async <ConfigType, ErrorType extends Error, InstanceType extends AxiosInstance>({
  callbackError,
  originalError,
  headers,
  config
}: TryRefreshAndRetryProps<ConfigType, ErrorType>) => {
  if (sessionStorage.getItem('refreshed')) {
    sessionStorage.removeItem('refreshed');
    callLogoutSSO();
    return Promise.reject(originalError);
  }

  const refresh_token = getRefreshToken();
  if (!refresh_token) return Promise.reject(originalError);

  sessionStorage.setItem('refreshed', 'true');
  return refreshToken(refresh_token)
    .toPromise()
    .then(response => {
      const { access_token, refresh_token: newRefreshToken } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', newRefreshToken);
      // Clear the one-shot sentinel after a successful refresh so the next genuine 401 in the same session
      // starts a fresh refresh cycle instead of forcing an immediate logout.
      sessionStorage.removeItem('refreshed');

      subscribers = [];
      return new Promise(resolve => {
        subscribeTokenRefresh(token => {
          const retriedRequest: ConfigType = {
            ...config,
            headers: {
              ...headers,
              'Accept-Language': 'pt-BR',
              Authorization: `Bearer ${token}`
            }
          };

          resolve(new AxiosObservable(retriedRequest as unknown as InstanceType));
        });

        onRefreshed(access_token);
      });
    })
    .catch((error: ErrorType) => {
      return callbackError(error);
    });
};

export async function handleResponseError(error: AxiosError, errorMsg: ErrorMsg) {
  const url = error.config?.url ?? '';
  const status = error?.response?.status;

  const URLS_TO_IGNORE_REFRESH_TOKEN = ['v2/accounts/me', '.amazonaws.com', 'v2/accounts'];
  const shouldSkipRefresh = URLS_TO_IGNORE_REFRESH_TOKEN.some(u => url.includes(u));
  // Only 401 triggers refresh+retry. A 403 means authenticated-but-forbidden (permission-denied), where
  // refreshing the token cannot help and the sticky `refreshed` sentinel would force a logout on the next request.
  if (!shouldSkipRefresh && status === 401) {
    return handleUnauthorized(error);
  }

  return Promise.reject(errorMsg);
}

interface LegacyErrorPayload {
  config?: AxiosRequestConfig;
  data?: { error_description?: string };
}

export function handleResponseErrorWithoutSSO({
  error,
  status,
  errorMsg
}: {
  errorMsg: ErrorMsg;
  error: LegacyErrorPayload;
  status: number;
}) {
  if (status === 401 && error?.data?.error_description !== 'Invalid user credentials') {
    const requestSubscribers = new Promise(resolve => {
      subscribeTokenRefresh((token: string) => {
        const originalRequest = {
          ...error.config,
          headers: {
            ...error.config?.headers,
            'Accept-Language': 'pt-BR',
            Authorization: `Bearer ${token}`
          }
        };
        resolve(new AxiosObservable(originalRequest as unknown as AxiosInstance));
      });
    });
    return requestSubscribers;
  }
  return Promise.reject(errorMsg);
}
